import {
  getAppEventMessage,
  registerEventDetailsView,
  useGetEventBySlugQuery,
  useGetEventLikesIdsQuery,
  useGetRequestStatus,
  useGetUserStructureQuery,
  useLikeEventMutation,
  useRequestParticipationMutation,
} from "@ac-expo/api";
import {
  EventDetails,
  isExhibitorProfilesPackPrestationType,
  OpenExponentConversationEvent,
  OpenExponentSocialsEvent,
  OpenExponentWebsiteEvent,
  ParticipationRequestType,
  TicketTypeItemType,
  trackedEvents,
} from "@ac-expo/api-types";
import {
  BusinessSectorMultiSelect,
  Button,
  EventNotOnline,
  EventView,
  InformationPanel,
  Loading,
  PlanningEvent,
  useIsMobile,
  useIsTablet,
  useToast,
} from "@ac-expo/ui-components";
import { isDateInRanges } from "@ac-expo/utils";
import { FunnelIcon, Heart } from "@phosphor-icons/react";
import EventNotFound from "@ui/components/ui/event/EventNotFound";
import {
  EventPanel,
  isEventPanel,
} from "@ui/components/ui/event/EventViewTabs";
import ExponentPanel, {
  ExponentPanelExponent,
} from "@ui/components/ui/event/Panel/ExponentPanel";
import ProgramPanel from "@ui/components/ui/event/Panel/ProgramPanel";
import environment from "@web-app/src/environments/environment";
import { useActivityDetails } from "@web-app/src/hooks/useActivityDetails";
import useTracking, { TrackingFn } from "@web-app/src/hooks/useTracking";
import AuthStore from "@web-app/src/services/AuthStore";
import { OPEN_CART_QUERY_PARAM } from "@web-app/src/services/sso-callback";
import { SearchStore } from "@web-app/src/services/SearchStore";
import { useFileUrlGetter } from "@web-app/src/shared/files";
import Search from "@web-app/src/shared/Search";
import { noop } from "lodash";
import { observer } from "mobx-react-lite";
import { usePathname, useRouter } from "next/navigation";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserContext } from "../../context/auth/user.context";
import { useCartContext } from "../../context/cart.context";
import { DetailsModal } from "../Animation/DetailsModal";
import ProtectedButton from "../Auth/components/ProtectedButton";
import CustomHead from "../CustomHead";
import Exhibitor from "../Exhibitor/Exhibitor";
import { canUnlockGroupTickets, GroupUnlockLine } from "../Ticket/GroupUnlock";
import Ticketing from "../Ticket/Ticketing";
import { BecomeExponent } from "./BecomeExponent";
import { EventReportDialog } from "./EventReportDialog";
import { EventScheduleSection } from "./EventScheduleSection";

const VIEWED_EVENTS_SESSION_KEY_PREFIX = "event-details-viewed-";

export const EventDetail = observer(({ eventSlug }: { eventSlug: string }) => {
  const router = useRouter();
  const pathname = usePathname();
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();

  const { toggleCart } = useCartContext();
  const { user, isLoading: isUserLoading, isAuthenticated } = useUserContext();
  const { toast } = useToast();
  const { track } = useTracking();
  const { data: event, isLoading, isError } = useGetEventBySlugQuery(eventSlug);
  const { openActivityList, closeActivityList, selectedActivities } =
    useActivityDetails();
  const [isBecomeExponentOpen, setIsBecomeExponentOpen] = useState(false);
  const [isTicketingModalOpen, setIsTicketingModalOpen] = useState(false);
  const [isGroupUnlockRequested, setIsGroupUnlockRequested] = useState(false);
  const [exhibitorId, setExhibitorId] = useQueryState(
    "exhibitorId",
    parseAsString.withOptions({
      history: isMobile ? "push" : "replace",
    })
  );
  const [panel, setPanel] = useQueryState("panel", parseAsString);
  const [openCartParam, setOpenCartParam] = useQueryState(
    OPEN_CART_QUERY_PARAM,
    parseAsString
  );
  const [activityIdParam, setActivityIdParam] = useQueryState("activityId", parseAsString);
  const [activityIdsParam, setActivityIdsParam] = useQueryState("activityIds", parseAsString);
  const [isEventReportModalOpen, setIsEventReportModalOpen] = useState(false);
  const [selectedBusinessSectorIds, setSelectedBusinessSectorIds] = useState<string[]>([]);

  const [ticketTypes, activityTicketTypes] = event?.ticketTypes.reduce(
    (acc, ticketType) => {
      if (ticketType.activityId === null) {
        acc[0].push(ticketType);
      } else {
        acc[1].push(ticketType);
      }
      return acc;
    },
    [[], []] as EventDetails["ticketTypes"][]
  ) ?? [[], []];
  const { getFileUrl } = useFileUrlGetter();
  const eventImageUrl = getFileUrl(event?.image?.key, event?.image?.type);

  const activePanel = panel && isEventPanel(panel) ? panel : "information";

  const isUserExhibitor =
    user?.roles?.some((role) => role.role === "exhibitor") ?? false;

  const eventId = event?.id ?? "";

  // count one consultation per event per browser session, including anonymous visitors
  useEffect(() => {
    if (!eventId) return;

    const viewedKey = `${VIEWED_EVENTS_SESSION_KEY_PREFIX}${eventId}`;
    if (sessionStorage.getItem(viewedKey)) return;

    sessionStorage.setItem(viewedKey, "true");
    registerEventDetailsView(eventId).catch(noop);
  }, [eventId]);

  useEffect(() => {
    if (openCartParam === "1") {
      toggleCart(true);
      setOpenCartParam(null);
    }
  }, [openCartParam, toggleCart, setOpenCartParam]);

  const { data: participationRequest } = useGetRequestStatus(
    eventId,
    isUserExhibitor
  );
  const { mutate } = useRequestParticipationMutation();
  const { data: structure } = useGetUserStructureQuery();
  const { data: likes } = useGetEventLikesIdsQuery();

  const { mutate: updateLike } = useLikeEventMutation();

  const participationRequestStatus = useMemo(():
    | ParticipationRequestType["status"]
    | null => {
    if (
      event &&
      structure &&
      event.eventStructures.find((es) => es.structure.id === structure.id)
    ) {
      return "accepted";
    }
    if (participationRequest) {
      return participationRequest.status;
    }
    return null;
  }, [event, participationRequest, structure]);

  const sendParticipationRequest = () => {
    const structureId = structure?.id;

    if (!isUserExhibitor || structureId === undefined) {
      setIsBecomeExponentOpen(true);
      return;
    }

    if (participationRequest) {
      toast({
        title: "Vous avez déjà envoyé une demande de participation",
      });
      return;
    }

    track(
      () =>
        mutate(
          { eventId },
          {
            onSuccess: () => {
              toast({
                title: "Demande de participation envoyée",
              });
            },
          }
        ),
      getAppEventMessage(trackedEvents.exponentParticipationRequest, {
        eventId,
        structureId,
      })
    );
  };

  const isItemAvailable = useCallback(
    (item: Pick<TicketTypeItemType, "userType">) =>
      !!user?.isProfessional ||
      item.userType === "visitor" ||
      item.userType === "all" ||
      item.userType === "group" ||
      item.userType === "exhibitor",
    [user?.isProfessional]
  );

  const {
    availableTicketTypes,
    availableBadgeTypes,
    availableActivityTicketTypes,
  } = useMemo(
    () => ({
      availableTicketTypes: ticketTypes.filter(isItemAvailable),
      availableBadgeTypes: event?.badgeTypes.filter(isItemAvailable) ?? [],
      availableActivityTicketTypes: activityTicketTypes.filter(isItemAvailable),
    }),
    [activityTicketTypes, event?.badgeTypes, isItemAvailable, ticketTypes]
  );
  const exponentSearchStore = useMemo(() => new SearchStore(), []);

  const authGuard = useCallback(
    (action: () => void, fallback?: () => void, ignoreModal = false) => {
      const isLoggedIn = !!user;

      if (isLoggedIn) {
        action();
        return;
      }
      fallback?.();

      if (!ignoreModal) {
        AuthStore.setPendingAction(action);
        AuthStore.setModalVisible(true);
      }
    },
    [user]
  );

  const hasProcessedActivityId = useRef(false);
  const isLiked = useMemo(
    () => likes?.some((like) => like === event?.id) ?? false,
    [event?.id, likes]
  );

  /* Note : mainly used when arriving from the embed program */
  useEffect(() => {
    if (
      (activityIdParam || activityIdsParam) &&
      event &&
      activePanel === "program" &&
      !hasProcessedActivityId.current &&
      !isUserLoading
    ) {
      let targetActivityIds: string[] = [];

      if (activityIdsParam) {
        targetActivityIds = activityIdsParam
          .split(",")
          .filter((id) => id.trim() !== "");
      } else if (activityIdParam) {
        targetActivityIds = [activityIdParam];
      }

      if (targetActivityIds.length > 0) {
        const activities = event.activities.filter((a) =>
          targetActivityIds.includes(a.id)
        );

        if (activities.length > 0) {
          hasProcessedActivityId.current = true;

          // Note : might not be the best way to do this
          const planningEvents: PlanningEvent[] = activities.map(
            (activity) => ({
              id: activity.id,
              activityId: activity.id,
              title: activity.name,
              start: new Date().toISOString(),
              end: new Date().toISOString(),
              allDay: false,
              isFree: activity.isFree,
              createdBy: activity.structureId ? "Exposant" : "Organisateur",
              isRegistrationRequired: activity.registrationRequired,
            })
          );

          setActivityIdParam(null);
          setActivityIdsParam(null);

          openActivityList(planningEvents);
        }
      }
    }
  }, [
    activityIdParam,
    activityIdsParam,
    event,
    activePanel,
    isUserLoading,
    authGuard,
    openActivityList,
    setActivityIdParam,
    setActivityIdsParam,
  ]);

  const activitiesList = useMemo(() => {
    return (
      event?.activities.filter((ea) =>
        selectedActivities.some((sa) => sa.activityId === ea.id)
      ) ?? []
    );
  }, [event, selectedActivities]);

  const exponentsWithImages: ExponentPanelExponent[] = useMemo(() => {
    if (!event) {
      return [];
    }

    return event.eventStructures.map((es): ExponentPanelExponent => {
      const exhibitorProfilesPack = es.exhibitorProfilesPack ?? null;
      const isPromoted =
        isDateInRanges(new Date(), es.promotionDateRanges ?? []) ?? false;

      if (
        exhibitorProfilesPack &&
        !isExhibitorProfilesPackPrestationType(exhibitorProfilesPack)
      ) {
        throw new Error("Invalid exhibitor profiles pack");
      }

      return {
        ...es.structure,
        imgUrl: getFileUrl(
          es.structure.picture?.key,
          es.structure.picture?.type
        ),
        exhibitorProfilesPack,
        isPromoted,
      };
    });
  }, [event, getFileUrl]);

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !event) {
    return <EventNotFound />;
  }

  const metaTitle = `${event.name} - Connect'Events`;
  const metaDescription = `${event.name}, par ${event.organiserName}. Trouve facilement les événements qui t'intéressent, où tu veux, quand tu veux ! Des expositions, des soirées, des salons ou toutes autres manifestations ! Connect’Events te permet de rester connecté aux événements de ta ville et d'ailleurs.`;

  const navigateBack = () => {
    router.back();
  };

  // The server already answers 404 for non-online events (pages/event/[slug]);
  // this covers client-side navigation to an event taken offline meanwhile.
  if (event.status !== "online") {
    return <EventNotOnline />;
  }

  const openTicketing = () => {
    setIsTicketingModalOpen(true);
  };

  const openGroupUnlock = () => {
    setIsGroupUnlockRequested(true);
    setIsTicketingModalOpen(true);
  };

  const handleTicketingModalOpenChange = (open: boolean) => {
    setIsTicketingModalOpen(open);

    if (!open) {
      setIsGroupUnlockRequested(false);
    }
  };

  const groupUnlockLine = canUnlockGroupTickets({
    event,
    isAuthenticated,
    user,
    ticketTypes,
  }) ? (
    <GroupUnlockLine onClick={openGroupUnlock} />
  ) : null;

  const openExponentDetails = (id: string) => {
    const eventStructure = event?.eventStructures.find(
      (es) => es.structure.id === id
    );

    const isDetailedExhibitorProfile = eventStructure?.exhibitorProfilesPack;

    if (!isDetailedExhibitorProfile || !eventStructure) {
      return;
    }

    return track(
      () => {
        setExhibitorId(id);
      },
      getAppEventMessage(trackedEvents.openExponentProfile, {
        structureId: id,
        eventId,
      })
    );
  };

  const openEventReport = () => {
    authGuard(() => setIsEventReportModalOpen(true));
  };

  const userType = !user?.isProfessional
    ? "visitor"
    : user.organiser !== null && user.organiser.id === event.organiserId
      ? "organiser"
      : "exhibitor";

  const openExponentConversation = (
    exponentId: string,
    structureId?: string
  ) => {
    const eventStructure = exponentId
      ? event?.eventStructures.find((es) => es.structure.adminId === exponentId)
      : event?.eventStructures.find((es) => es.structure.id === structureId);

    authGuard(() =>
      track(
        () => router.push(`/conversations?participantId=${exponentId}`),
        getAppEventMessage(trackedEvents.openExponentConversation, {
          structureId: eventStructure?.structure.id ?? "",
          eventId,
        })
      )
    );
  };

  const onSocialsClick = () => {
    track(
      () => noop(),
      getAppEventMessage(trackedEvents.openEventSocials, {
        eventId: event.id,
      })
    );
  };

  const onWebsiteClick = () => {
    track(
      () => noop(),
      getAppEventMessage(trackedEvents.openEventWebsite, {
        eventId: event.id,
      })
    );
  };

  const onConversationClick = () => {
    track(
      () => noop(),
      getAppEventMessage(trackedEvents.openOrganizerConversation, {
        organizerId: event.organiser.id,
      })
    );
  };

  const onOpenActivityList = (events: PlanningEvent[]) =>
    openActivityList(events);

  const onOpenExhibitorList = () => {
    track(
      () => noop(),
      getAppEventMessage(trackedEvents.openExponentList, {
        eventId: event.id,
        structureIds: event.eventStructures.map((es) => es.structure.id),
        structureId: "",
      })
    );
  };

  const onPanelChange = (to: EventPanel) => {
    if (to === "exponents") {
      onOpenExhibitorList();
    }
    setPanel(to);
  };

  // the consultation is counted by the destination event page itself
  const onEventDetailsClick = () => {
    setExhibitorId(null);
  };

  const hasAnyTicketType =
    availableTicketTypes.length > 0 ||
    availableBadgeTypes.length > 0 ||
    availableActivityTicketTypes.length > 0;

  // An event whose only tickets are reserved to groups shows nothing public: the unlock
  // line is then the whole ticketing entry point, and the card must still be rendered.
  const hasTicketingEntryPoint = hasAnyTicketType || !!groupUnlockLine;

  const isRegisterEnabled = hasAnyTicketType && event.isTicketingAvailable;

  const getWebAppUrl = () => {
    return `${environment.NEXT_BASE_URL}${pathname}`;
  };

  const toggleFavorite = (eventId: string) => {
    updateLike(
      { eventId, like: !isLiked },
      {
        onSuccess: () => {
          toast({
            title: isLiked
              ? "Evénement retiré des favoris"
              : "Evénement ajouté aux favoris",
          });
        },
      }
    );
  };

  return (
    <>
      <CustomHead
        title={metaTitle}
        content={metaDescription}
        pageSlug={`/event/${eventSlug}`}
        pageType="event"
        ogImage={eventImageUrl}
      />
      <EventView
        activityTicketTypes={availableActivityTicketTypes}
        badgeTypes={availableBadgeTypes}
        ticketTypes={availableTicketTypes}
        as={userType}
        event={event}
        participationRequestStatus={participationRequestStatus}
        navigateBack={navigateBack}
        thumbnailImageUrl={eventImageUrl || "/images/event.jpeg"}
        backgroundImageUrl={
          getFileUrl(
            event.customEventAppearance?.backgroundPicture?.key,
            "media"
          ) || ""
        }
        openTicketing={openTicketing}
        groupUnlockLine={groupUnlockLine}
        sendParticipationRequest={sendParticipationRequest}
        isTicketingVisible={!isTablet && hasTicketingEntryPoint}
        onPanelChange={onPanelChange}
        activePanel={activePanel}
        panels={{
          information: (
            <InformationPanel
              as={userType}
              event={event}
              isTicketingVisible={!!isTablet && hasTicketingEntryPoint}
              onConversationClick={onConversationClick}
              actionGuard={authGuard}
              onSocialsClick={onSocialsClick}
              onWebsiteClick={onWebsiteClick}
              onOpenTicketing={openTicketing}
              groupUnlockLine={groupUnlockLine}
              onRequestParticipation={sendParticipationRequest}
              participationRequestStatus={participationRequestStatus}
              pictures={event.pictures}
              getFileUrl={getFileUrl}
              scheduleSection={<EventScheduleSection event={event} />}
            />
          ),
          program: (
            <ProgramPanel
              activities={event.activities}
              eventSchedules={event.dates?.schedules}
              openActivityList={onOpenActivityList}
              isRegisterEnabled={isRegisterEnabled}
              customCalendar={event.customCalendar ?? undefined}
              getFileUrl={getFileUrl}
            />
          ),
          exponents: (
            <ExponentPanel
              SearchBar={Search}
              eventId={event.id}
              exponents={exponentsWithImages}
              searchStore={exponentSearchStore}
              actionGuard={authGuard}
              openExponentConversation={openExponentConversation}
              openExponentDetails={openExponentDetails}
              selectedBusinessSectorIds={selectedBusinessSectorIds}
              businessSectorFilter={
                <BusinessSectorMultiSelect
                  selectedIds={selectedBusinessSectorIds}
                  onConfirm={setSelectedBusinessSectorIds}
                  eventId={event.id}
                  confirmLabel="Appliquer"
                  resetLabel="Réinitialiser"
                >
                  <Button variant="outline-white" className="gap-1">
                    <FunnelIcon size={16} weight="bold" />
                    Filtres
                    {selectedBusinessSectorIds.length > 0 &&
                      ` (${selectedBusinessSectorIds.length})`}
                  </Button>
                </BusinessSectorMultiSelect>
              }
            />
          ),
        }}
        model={event.customEventAppearance?.model ?? 0}
        backgroundColor={
          event.customEventAppearance?.backgroundColor ?? "white"
        }
        key={event.customEventAppearance?.backgroundPicture?.key}
        opacity={event.customEventAppearance?.opacity ?? 100}
        getWebAppUrl={getWebAppUrl}
        toggleFavoriteButton={
          <ProtectedButton>
            <Button
              variant={"outline-red"}
              type="button"
              onClick={() => toggleFavorite(event.id)}
            >
              <Heart
                weight={isLiked ? "fill" : "regular"}
                size={16}
                className="text-secondary-700"
              />
            </Button>
          </ProtectedButton>
        }
      />
      <Ticketing
        isTicketingModalOpen={isTicketingModalOpen}
        setIsTicketingModalOpen={handleTicketingModalOpenChange}
        event={event}
        eventSlug={eventSlug}
        isProfessional={user?.isProfessional ?? false}
        onAddToCartClose={() => toggleCart(true)}
        hideTrigger
        isGroupUnlockRequested={isGroupUnlockRequested}
      />
      <BecomeExponent
        isOpen={isBecomeExponentOpen}
        setIsOpen={setIsBecomeExponentOpen}
      />
      {activitiesList.length !== 0 ? (
        <DetailsModal
          open={activitiesList.length !== 0}
          setOpen={(open) => !open && closeActivityList()}
          activities={activitiesList}
        />
      ) : null}
      {exhibitorId && (
        <Exhibitor
          exhibitorId={exhibitorId}
          eventId={event.id}
          eventSchedules={event?.dates?.schedules}
          isOpen
          onClose={() => setExhibitorId(null)}
          getAppEventClickHandler={(appEvent) =>
            getAppEventClickHandler(exhibitorId, event.id, track, appEvent)
          }
          onEventParticipationCardClick={onEventDetailsClick}
          actionGuard={authGuard}
        />
      )}
      <EventReportDialog
        eventId={event.id}
        isEventReportModalOpen={isEventReportModalOpen}
        setIsEventReportModalOpen={setIsEventReportModalOpen}
      />
      <div className="flex w-full justify-center bg-white p-4 pt-8 text-center">
        <button
          onClick={openEventReport}
          className="text-sm font-light italic text-gray-500 underline"
        >
          Signaler l&apos;événement
        </button>
      </div>
    </>
  );
});

export const getAppEventClickHandler = (
  structureId: string,
  eventId: string,
  track: TrackingFn,
  appEventName: (
    | OpenExponentConversationEvent
    | OpenExponentWebsiteEvent
    | OpenExponentSocialsEvent
  )["name"]
) => {
  return () =>
    track(
      () => noop(),
      getAppEventMessage(appEventName, {
        structureId,
        eventId,
      })
    );
};

export default EventDetail;
